import React, { useEffect, useState } from 'react';
import { Modal } from '../Modal';
import { Button } from '../Button';
import { Loader2 } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { MarkdownContent } from '../MarkdownContent';
import termsContent from '../../../assets/legal/terms-content.md';
import { fetchActiveTermsVersion } from '../../../services/terms';

export interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept?: () => void;
  showAcceptButton?: boolean;
  className?: string;
}

export const TermsModal: React.FC<TermsModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  showAcceptButton = false,
  className
}) => {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState<string>('');
  const [version, setVersion] = useState<string>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      fetchTermsContent();
    }
  }, [isOpen]);

  const fetchTermsContent = async () => {
    setLoading(true);
    setError('');

    try {
      const terms = await fetchActiveTermsVersion();

      if (!terms) {
        throw new Error('No active terms version found');
      }

      setContent(terms.content);
      setVersion(terms.version);
    } catch (err: any) {
      console.error('Error fetching terms:', err);
      // Fallback to local markdown file
      try {
        const localResponse = await fetch(termsContent);
        const text = await localResponse.text();
        setContent(text);
        setVersion('Local');
      } catch (fallbackErr) {
        setError('Failed to load Terms & Conditions. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = () => {
    if (onAccept) {
      onAccept();
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Terms and Conditions"
      description={version ? `Version ${version}` : ''}
      className={cn('max-w-[calc(100vw-1rem)] sm:max-w-lg md:max-w-2xl lg:max-w-3xl max-h-[85vh] sm:max-h-[80vh]', className)}
    >
      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        ) : (
          <>
            {/* Scrollable content area */}
            <div className="max-h-[60vh] sm:max-h-[55vh] overflow-y-auto px-3 sm:px-4 py-2 bg-background border border-border rounded-md">
              <MarkdownContent content={content} />
            </div>

            {/* Action buttons */}
            <div className="flex gap-3 justify-end pt-4 border-t border-border">
              {showAcceptButton && (
                <>
                  <Button
                    variant="outline"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAccept}
                    className="font-semibold"
                  >
                    I Accept
                  </Button>
                </>
              )}
              {!showAcceptButton && (
                <Button onClick={onClose}>
                  Close
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default TermsModal;
